import type { Map, MapGeoJSONFeature, MapMouseEvent } from 'maplibre-gl';

import type { MapLayerKey, MapLayerState } from '@/stores/MapLayersContext';
import { ROUTE_VARIANT_KEYS, type RouteVariantKey } from '@/types/routes';

import {
  BARRIERS_ICON_HOVERED_LAYER_ID,
  BARRIERS_ICON_LAYER_ID,
  BARRIERS_ICON_SELECTED_LAYER_ID,
  BARRIERS_SOURCE_ID,
  BARRIERS_WAYPOINT_LAYER_ID,
  BARRIERS_ZONE_HOVERED_LAYER_ID,
  BARRIERS_ZONE_LAYER_ID,
  BARRIERS_ZONE_RESIDUAL_LAYER_ID,
  BARRIERS_ZONE_SELECTED_LAYER_ID,
  buildBarriersSource,
  buildIconHoveredLayer,
  buildIconSelectedLayer,
  buildIconSymbolLayer,
  buildWaypointFlagLayer,
  buildZoneFillLayer,
  buildZoneHoveredLayer,
  buildZoneResidualLayer,
  buildZoneSelectedLayer,
} from './barriersLayer';
import {
  DETOUR_WAYPOINTS_HOVERED_LAYER_ID,
  DETOUR_WAYPOINTS_LAYER_ID,
  DETOUR_WAYPOINTS_SOURCE_ID,
  buildDetourWaypointsHoveredLayer,
  buildDetourWaypointsLayer,
  buildDetourWaypointsSource,
} from './detourWaypointsLayer';
import {
  BARRIER_SELECTED_WAYPOINTS_LAYER_ID,
  BARRIER_SELECTED_WAYPOINTS_SOURCE_ID,
  buildBarrierSelectedWaypointsLayer,
  buildBarrierSelectedWaypointsSource,
} from './barrierSelectedWaypointsLayer';
import {
  INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID,
  INDUSTRIAL_REF_BOOK_LAYER_ID,
  INDUSTRIAL_REF_BOOK_SOURCE_ID,
  buildIndustrialRefBookHoveredLayer,
  buildIndustrialRefBookLayer,
  buildIndustrialRefBookSource,
} from './industrialRefBookLayer';
import {
  FREIGHT_RESTRICTIONS_HOVER_LAYER_ID,
  FREIGHT_RESTRICTIONS_LINE_LAYER_ID,
  FREIGHT_RESTRICTIONS_SIGN_LAYER_ID,
  FREIGHT_RESTRICTIONS_SOURCE_ID,
  buildFreightRestrictionsHoverLayer,
  buildFreightRestrictionsLineLayer,
  buildFreightRestrictionsSignLayer,
  buildFreightRestrictionsSource,
} from './freightRestrictionsLayer';
import { loadFreightRestrictionIcons } from './freightRestrictionIcons';
import {
  DRAFT_HINT_LINE_LAYER_ID,
  DRAFT_ICON_LAYER_ID,
  DRAFT_SOURCE_ID,
  DRAFT_WAYPOINTS_LAYER_ID,
  buildDraftHintLineLayer,
  buildDraftIconLayer,
  buildDraftSource,
  buildDraftWaypointsLayer,
} from './draftLayer';
import {
  ROUTE_DRAFT_LABELS_LAYER_ID,
  ROUTE_DRAFT_LINE_LAYER_ID,
  ROUTE_DRAFT_MARKERS_LAYER_ID,
  ROUTE_DRAFT_SOURCE_ID,
  buildRouteDraftLabelsLayer,
  buildRouteDraftLineLayer,
  buildRouteDraftMarkersLayer,
  buildRouteDraftSource,
} from './routeDraftLayer';
import {
  VARIANT_LAYER_SPECS,
  buildVariantHighlightLayer,
  buildVariantHoveredLayer,
  buildVariantLineLayer,
  buildVariantSource,
} from './routesGeoJsonLayer';

/**
 * Layer ids for each variant's pair (line + highlight), in install order.
 * Keeping this list here lets callers iterate without caring about the
 * specific layer ids.
 */
export const VARIANT_LINE_LAYERS: readonly string[] = ROUTE_VARIANT_KEYS.map(
  (v) => VARIANT_LAYER_SPECS[v].lineLayerId,
);

export const VARIANT_HIGHLIGHT_LAYERS: readonly string[] = ROUTE_VARIANT_KEYS.map(
  (v) => VARIANT_LAYER_SPECS[v].highlightLayerId,
);

/**
 * Maps logical `MapLayerKey` (the legend's entries) to the set of
 * MapLibre layer ids that a given key owns. Used by both visibility
 * and z-order updates — the visibility toggle hides every owned layer;
 * reordering moves the whole group together.
 */
export const MAP_LAYER_GROUPS: Readonly<Record<MapLayerKey, readonly string[]>> = {
  barriers: [
    BARRIERS_ZONE_LAYER_ID,
    BARRIERS_ZONE_SELECTED_LAYER_ID,
    BARRIERS_ZONE_RESIDUAL_LAYER_ID,
    BARRIERS_ZONE_HOVERED_LAYER_ID,
    BARRIERS_ICON_LAYER_ID,
    BARRIERS_ICON_SELECTED_LAYER_ID,
    BARRIERS_ICON_HOVERED_LAYER_ID,
    BARRIERS_WAYPOINT_LAYER_ID,
    BARRIER_SELECTED_WAYPOINTS_LAYER_ID,
  ],
  detours: [DETOUR_WAYPOINTS_LAYER_ID, DETOUR_WAYPOINTS_HOVERED_LAYER_ID],
  routes_barrier_aware: [
    VARIANT_LAYER_SPECS.barrier_aware.lineLayerId,
    VARIANT_LAYER_SPECS.barrier_aware.hoveredLayerId,
    VARIANT_LAYER_SPECS.barrier_aware.highlightLayerId,
  ],
  routes_gis: [
    VARIANT_LAYER_SPECS.gis.lineLayerId,
    VARIANT_LAYER_SPECS.gis.hoveredLayerId,
    VARIANT_LAYER_SPECS.gis.highlightLayerId,
  ],
  routes_iteko: [
    VARIANT_LAYER_SPECS.iteko.lineLayerId,
    VARIANT_LAYER_SPECS.iteko.hoveredLayerId,
    VARIANT_LAYER_SPECS.iteko.highlightLayerId,
  ],
  industrial_ref_book: [
    INDUSTRIAL_REF_BOOK_LAYER_ID,
    INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID,
  ],
  freight_restrictions: [
    FREIGHT_RESTRICTIONS_LINE_LAYER_ID,
    FREIGHT_RESTRICTIONS_HOVER_LAYER_ID,
    FREIGHT_RESTRICTIONS_SIGN_LAYER_ID,
  ],
};

/**
 * For every legend entry, the single MapLibre layer whose features the
 * ID/select tool treats as "objects of that legend entry". A barrier
 * group owns both a zone and an icon, but only the icon is clickable —
 * fills are huge and would hijack clicks on whatever lives under them.
 */
const INTERACTABLE_LAYER_FOR_KEY: Readonly<Record<MapLayerKey, string>> = {
  barriers: BARRIERS_ICON_LAYER_ID,
  detours: DETOUR_WAYPOINTS_LAYER_ID,
  routes_barrier_aware: VARIANT_LAYER_SPECS.barrier_aware.lineLayerId,
  routes_gis: VARIANT_LAYER_SPECS.gis.lineLayerId,
  routes_iteko: VARIANT_LAYER_SPECS.iteko.lineLayerId,
  industrial_ref_book: INDUSTRIAL_REF_BOOK_LAYER_ID,
  freight_restrictions: FREIGHT_RESTRICTIONS_LINE_LAYER_ID,
};

export function installMapSources(
  map: Map,
  apiBaseUrl: string,
  barriersRevision: number,
): void {
  _installBarriers(map, apiBaseUrl, barriersRevision);
  _installRouteVariants(map);
  _installDetours(map);
  _installBarrierSelectedWaypoints(map);
  _installIndustrialRefBook(map, apiBaseUrl);
  _installFreightRestrictions(map, apiBaseUrl);
  _installBarrierDraft(map);
  _installRouteDraft(map);
}

function _installBarrierSelectedWaypoints(map: Map): void {
  if (!map.getSource(BARRIER_SELECTED_WAYPOINTS_SOURCE_ID)) {
    map.addSource(
      BARRIER_SELECTED_WAYPOINTS_SOURCE_ID,
      buildBarrierSelectedWaypointsSource(),
    );
  }
  if (!map.getLayer(BARRIER_SELECTED_WAYPOINTS_LAYER_ID)) {
    map.addLayer(buildBarrierSelectedWaypointsLayer());
  }
}

function _installBarriers(
  map: Map,
  apiBaseUrl: string,
  barriersRevision: number,
): void {
  if (!map.getSource(BARRIERS_SOURCE_ID)) {
    map.addSource(
      BARRIERS_SOURCE_ID,
      buildBarriersSource(apiBaseUrl, barriersRevision),
    );
  }
  if (!map.getLayer(BARRIERS_ZONE_LAYER_ID)) {
    map.addLayer(buildZoneFillLayer());
  }
  if (!map.getLayer(BARRIERS_ZONE_SELECTED_LAYER_ID)) {
    map.addLayer(buildZoneSelectedLayer(-1));
  }
  if (!map.getLayer(BARRIERS_ZONE_RESIDUAL_LAYER_ID)) {
    map.addLayer(buildZoneResidualLayer());
  }
  if (!map.getLayer(BARRIERS_ZONE_HOVERED_LAYER_ID)) {
    map.addLayer(buildZoneHoveredLayer());
  }
  if (!map.getLayer(BARRIERS_ICON_LAYER_ID)) {
    map.addLayer(buildIconSymbolLayer());
  }
  if (!map.getLayer(BARRIERS_ICON_SELECTED_LAYER_ID)) {
    map.addLayer(buildIconSelectedLayer(-1));
  }
  if (!map.getLayer(BARRIERS_ICON_HOVERED_LAYER_ID)) {
    map.addLayer(buildIconHoveredLayer());
  }
  if (!map.getLayer(BARRIERS_WAYPOINT_LAYER_ID)) {
    map.addLayer(buildWaypointFlagLayer());
  }
}

function _installRouteVariants(map: Map): void {
  for (const variant of ROUTE_VARIANT_KEYS) {
    const spec = VARIANT_LAYER_SPECS[variant];
    if (!map.getSource(spec.sourceId)) {
      map.addSource(spec.sourceId, buildVariantSource());
    }
    if (!map.getLayer(spec.lineLayerId)) {
      map.addLayer(buildVariantLineLayer(variant));
    }
    if (!map.getLayer(spec.hoveredLayerId)) {
      map.addLayer(buildVariantHoveredLayer(variant));
    }
    if (!map.getLayer(spec.highlightLayerId)) {
      map.addLayer(buildVariantHighlightLayer(variant, null));
    }
  }
}

function _installDetours(map: Map): void {
  if (!map.getSource(DETOUR_WAYPOINTS_SOURCE_ID)) {
    map.addSource(DETOUR_WAYPOINTS_SOURCE_ID, buildDetourWaypointsSource());
  }
  if (!map.getLayer(DETOUR_WAYPOINTS_LAYER_ID)) {
    map.addLayer(buildDetourWaypointsLayer());
  }
  if (!map.getLayer(DETOUR_WAYPOINTS_HOVERED_LAYER_ID)) {
    map.addLayer(buildDetourWaypointsHoveredLayer());
  }
}

function _installIndustrialRefBook(map: Map, apiBaseUrl: string): void {
  if (!map.getSource(INDUSTRIAL_REF_BOOK_SOURCE_ID)) {
    map.addSource(
      INDUSTRIAL_REF_BOOK_SOURCE_ID,
      buildIndustrialRefBookSource(apiBaseUrl),
    );
  }
  if (!map.getLayer(INDUSTRIAL_REF_BOOK_LAYER_ID)) {
    map.addLayer(buildIndustrialRefBookLayer());
  }
  if (!map.getLayer(INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID)) {
    map.addLayer(buildIndustrialRefBookHoveredLayer());
  }
}

function _installFreightRestrictions(map: Map, apiBaseUrl: string): void {
  // Fire-and-forget: rasterising 12 small SVGs takes ~5-10 ms total
  // but we do not need to block the rest of the install. The symbol
  // layer simply renders no glyph until its image is registered.
  void loadFreightRestrictionIcons(map);
  if (!map.getSource(FREIGHT_RESTRICTIONS_SOURCE_ID)) {
    map.addSource(
      FREIGHT_RESTRICTIONS_SOURCE_ID,
      buildFreightRestrictionsSource(apiBaseUrl),
    );
  }
  if (!map.getLayer(FREIGHT_RESTRICTIONS_LINE_LAYER_ID)) {
    map.addLayer(buildFreightRestrictionsLineLayer());
  }
  if (!map.getLayer(FREIGHT_RESTRICTIONS_HOVER_LAYER_ID)) {
    map.addLayer(buildFreightRestrictionsHoverLayer());
  }
  if (!map.getLayer(FREIGHT_RESTRICTIONS_SIGN_LAYER_ID)) {
    map.addLayer(buildFreightRestrictionsSignLayer());
  }
}

function _installBarrierDraft(map: Map): void {
  if (!map.getSource(DRAFT_SOURCE_ID)) {
    map.addSource(DRAFT_SOURCE_ID, buildDraftSource());
  }
  if (!map.getLayer(DRAFT_HINT_LINE_LAYER_ID)) {
    map.addLayer(buildDraftHintLineLayer());
  }
  if (!map.getLayer(DRAFT_WAYPOINTS_LAYER_ID)) {
    map.addLayer(buildDraftWaypointsLayer());
  }
  if (!map.getLayer(DRAFT_ICON_LAYER_ID)) {
    map.addLayer(buildDraftIconLayer());
  }
}

function _installRouteDraft(map: Map): void {
  if (!map.getSource(ROUTE_DRAFT_SOURCE_ID)) {
    map.addSource(ROUTE_DRAFT_SOURCE_ID, buildRouteDraftSource());
  }
  if (!map.getLayer(ROUTE_DRAFT_LINE_LAYER_ID)) {
    map.addLayer(buildRouteDraftLineLayer());
  }
  if (!map.getLayer(ROUTE_DRAFT_MARKERS_LAYER_ID)) {
    map.addLayer(buildRouteDraftMarkersLayer());
  }
  if (!map.getLayer(ROUTE_DRAFT_LABELS_LAYER_ID)) {
    map.addLayer(buildRouteDraftLabelsLayer());
  }
}

export function applyMapLayerState(
  map: Map,
  layers: readonly MapLayerState[],
): void {
  _applyVisibility(map, layers);
  _applyZOrder(map, layers);
}

function _applyVisibility(map: Map, layers: readonly MapLayerState[]): void {
  for (const layer of layers) {
    const visibility = layer.visible && !layer.disabled ? 'visible' : 'none';
    for (const layerId of MAP_LAYER_GROUPS[layer.key]) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    }
  }
}

function _applyZOrder(map: Map, layers: readonly MapLayerState[]): void {
  // Legend order is "top-first": the first entry should draw LAST
  // (= on top of everything else). MapLibre's `moveLayer(id, beforeId?)`
  // moves layer `id` just below `beforeId`; passing `undefined` puts
  // it on top of the stack. We walk the legend in reverse so the layer
  // at index 0 ends up last-moved — i.e., topmost.
  const reversed = [...layers].reverse();
  for (const layer of reversed) {
    for (const layerId of MAP_LAYER_GROUPS[layer.key]) {
      if (map.getLayer(layerId)) {
        map.moveLayer(layerId);
      }
    }
  }
  // Editor transient layers (barrier/route draft, suggestion markers)
  // MUST stay above the legend-managed groups so the user's in-progress
  // edits remain visible over existing geometry. Move them last.
  for (const layerId of EDITOR_LAYER_IDS) {
    if (map.getLayer(layerId)) {
      map.moveLayer(layerId);
    }
  }
}

const EDITOR_LAYER_IDS: readonly string[] = [
  DRAFT_HINT_LINE_LAYER_ID,
  DRAFT_WAYPOINTS_LAYER_ID,
  DRAFT_ICON_LAYER_ID,
  ROUTE_DRAFT_LINE_LAYER_ID,
  ROUTE_DRAFT_MARKERS_LAYER_ID,
  ROUTE_DRAFT_LABELS_LAYER_ID,
];

export interface MapHitResult {
  readonly barrierId: number | null;
  readonly routeId: string | null;
  readonly routeVariant: RouteVariantKey | null;
  readonly detourBarrierId: number | null;
  readonly industrialRefBookId: number | null;
  readonly freightRestrictionWayId: number | null;
}

export function queryMapHit(
  map: Map,
  event: MapMouseEvent,
  mapLayers: readonly MapLayerState[],
): MapHitResult {
  // Walk the legend top-first. The first legend entry whose interactable
  // layer has a feature under `event.point` wins — a plain identify-tool
  // fall-through. Hidden or disabled entries are skipped. We query each
  // layer separately instead of relying on MapLibre's render-order
  // tie-break, so the hit-test stays correct even in transient states
  // (e.g. during reorder when `_applyZOrder` hasn't synced z-order yet).
  for (const layer of mapLayers) {
    if (!layer.visible || layer.disabled) continue;
    const interactableId = INTERACTABLE_LAYER_FOR_KEY[layer.key];
    if (!map.getLayer(interactableId)) continue;
    const features: MapGeoJSONFeature[] = map.queryRenderedFeatures(
      event.point,
      { layers: [interactableId] },
    );
    const top = features[0];
    if (!top) continue;
    return _featureToHit(top);
  }
  return _emptyHit({});
}

function _featureToHit(feature: MapGeoJSONFeature): MapHitResult {
  const layerId = feature.layer.id;
  const props = feature.properties ?? {};

  if (layerId === BARRIERS_ICON_LAYER_ID) {
    const id = _toNumber(props.id);
    return id !== null ? _emptyHit({ barrierId: id }) : _emptyHit({});
  }
  if (layerId === DETOUR_WAYPOINTS_LAYER_ID) {
    const id = _toNumber(props.barrier_id);
    return id !== null ? _emptyHit({ detourBarrierId: id }) : _emptyHit({});
  }
  if (layerId === INDUSTRIAL_REF_BOOK_LAYER_ID) {
    const id = _toNumber(props.id);
    return id !== null ? _emptyHit({ industrialRefBookId: id }) : _emptyHit({});
  }
  if (layerId === FREIGHT_RESTRICTIONS_LINE_LAYER_ID) {
    const id = _toNumber(props.way_id);
    return id !== null
      ? _emptyHit({ freightRestrictionWayId: id })
      : _emptyHit({});
  }
  for (const variant of ROUTE_VARIANT_KEYS) {
    if (VARIANT_LAYER_SPECS[variant].lineLayerId !== layerId) continue;
    const routeId = typeof props.route_id === 'string' ? props.route_id : null;
    if (routeId === null) return _emptyHit({});
    return _emptyHit({ routeId, routeVariant: variant });
  }
  return _emptyHit({});
}

function _toNumber(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

function _emptyHit(overrides: Partial<MapHitResult>): MapHitResult {
  return {
    barrierId: null,
    routeId: null,
    routeVariant: null,
    detourBarrierId: null,
    industrialRefBookId: null,
    freightRestrictionWayId: null,
    ...overrides,
  };
}
